import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getDocumentsRequiringReacceptance } from "@/lib/legal-utils";

export interface DocumentRequiringAcceptance {
  document_type: string;
  version: string;
  published_at: string;
  requires_reacceptance: boolean;
  change_summary: string | null;
}

/**
 * Hook to check if user needs to re-accept any legal documents
 * Runs on login and app mount
 */
export function useLegalVersionCheck(userId: string | null) {
  return useQuery({
    queryKey: ["legal-version-check", userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const documents = await getDocumentsRequiringReacceptance(userId);
      return documents;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to get user's legal acceptance history
 */
export function useLegalAcceptances(userId: string | null) {
  return useQuery({
    queryKey: ["legal-acceptances", userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from("legal_acceptances")
        .select("document_type, version, accepted_at")
        .eq("user_id", userId)
        .order("accepted_at", { ascending: false });
      
      if (error) {
        console.error("Error fetching acceptances:", error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}
